import React, { useMemo, useState } from "react";
import { Table } from "antd";
import { type ColumnsType } from "antd/es/table";
import type {
  LinkedTrialExecutionStatus,
  LinkedTrialRow,
  TrialInsights,
} from "../../../pages/new-trail/types";

interface LinkedTrialsSectionProps {
  trialInsights?: TrialInsights;
  farmCount?: number;
  fieldCount?: number;
  linkedTrials?: LinkedTrialRow[];
  onTrialClick?: (trialId: string, trialName: string) => void;
}

const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    className={
      expanded
        ? "linked-trials-toggle-icon linked-trials-toggle-icon--expanded"
        : "linked-trials-toggle-icon"
    }
    width={18}
    height={18}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.5 10.7212L5.5575 11.7787L9 8.34375L12.4425 11.7787L13.5 10.7212L9 6.22125L4.5 10.7212Z"
      fill="#4D5165"
    />
  </svg>
);

const STATUS_LABELS: Record<LinkedTrialExecutionStatus, string> = {
  activated: "Activated",
  ready: "Ready",
  draft: "Draft",
};

// Kept inline rather than moved to Less: these feed antd's onHeaderCell/onCell,
// which spread the returned props straight onto the <th>/<td> DOM nodes.
// A className there competes with antd's own (fairly specific) default cell
// rules, where an inline style always wins deterministically.
const headerCellStyle: React.CSSProperties = {
  backgroundColor: "#F3F4F6",
  padding: "8px 16px",
  textAlign: "left",
};

const bodyCellStyle: React.CSSProperties = {
  padding: "10px 16px",
  height: 40,
};

const LinkedTrialsSection: React.FC<LinkedTrialsSectionProps> = ({
  trialInsights,
  farmCount = 0,
  fieldCount = 0,
  linkedTrials = [],
  onTrialClick,
}) => {
  const [expanded, setExpanded] = useState(false);
  const counts = trialInsights?.execution_status_summary ?? {
    activated: 0,
    ready: 0,
    draft: 0,
  };
  const totalTrials = trialInsights?.total_trials ?? 0;

  const columns: ColumnsType<LinkedTrialRow> = useMemo(
    () => [
      {
        title: <span className="linked-trials-section__col-header">Trial</span>,
        dataIndex: "name",
        key: "name",
        onHeaderCell: () => ({ style: headerCellStyle }),
        onCell: () => ({ style: bodyCellStyle }),
        render: (name: string, record: LinkedTrialRow) => (
          <button
            type="button"
            className="linked-trials-section__trial-link"
            onClick={() => onTrialClick?.(record.id, name)}
          >
            {name}
          </button>
        ),
      },
      {
        title: (
          <div className="linked-trials-section__col-header-stack">
            <span className="linked-trials-section__col-header-line linked-trials-section__col-header-line--primary">
              Field
            </span>
            <span className="linked-trials-section__col-header-line linked-trials-section__col-header-line--secondary">
              Farm
            </span>
          </div>
        ),
        dataIndex: "field_name",
        key: "fieldFarm",
        onHeaderCell: () => ({ style: headerCellStyle }),
        onCell: () => ({ style: bodyCellStyle }),
        render: (_value: string | null, record: LinkedTrialRow) => (
          <div>
            <div className="linked-trials-section__cell-primary">
              {record.field_name ?? "—"}
            </div>
            <div className="linked-trials-section__stat-label linked-trials-section__stat-label--muted">
              {record.property_name ?? "—"}
            </div>
          </div>
        ),
      },
      {
        title: (
          <span className="linked-trials-section__col-header">Status</span>
        ),
        dataIndex: "execution_status",
        key: "execution_status",
        onHeaderCell: () => ({ style: headerCellStyle }),
        onCell: () => ({ style: bodyCellStyle }),
        render: (status: LinkedTrialExecutionStatus) => (
          <span
            className={`linked-trials-section__status-tag linked-trials-section__status-tag--${status}`}
          >
            {STATUS_LABELS[status]}
          </span>
        ),
      },
    ],
    [onTrialClick]
  );

  return (
    <div className="linked-trials-section">
      <h3 className="linked-trials-section__title">
        Linked Trials ({totalTrials})
      </h3>

      <div className="linked-trials-section__pills">
        <div className="linked-trials-section__stat-box linked-trials-section__stat-box--activated">
          <div className="linked-trials-section__stat-number linked-trials-section__stat-number--activated">
            {counts.activated}
          </div>
          <div className="linked-trials-section__stat-label linked-trials-section__stat-label--activated">
            Activated
          </div>
        </div>
        <div className="linked-trials-section__stat-box linked-trials-section__stat-box--ready">
          <div className="linked-trials-section__stat-number linked-trials-section__stat-number--ready">
            {counts.ready}
          </div>
          <div className="linked-trials-section__stat-label linked-trials-section__stat-label--ready">
            Ready
          </div>
        </div>
        <div className="linked-trials-section__stat-box linked-trials-section__stat-box--draft">
          <div className="linked-trials-section__stat-number linked-trials-section__stat-number--draft">
            {counts.draft}
          </div>
          <div className="linked-trials-section__stat-label linked-trials-section__stat-label--draft">
            Draft
          </div>
        </div>
      </div>

      <h3 className="linked-trials-section__title linked-trials-section__title--spaced-top">
        Coverage
      </h3>

      <div className="linked-trials-section__pills">
        <div className="linked-trials-section__stat-box linked-trials-section__stat-box--neutral">
          <div className="linked-trials-section__coverage-label">Farms</div>
          <div className="linked-trials-section__stat-number linked-trials-section__stat-number--neutral">
            {farmCount}
          </div>
        </div>
        <div className="linked-trials-section__stat-box linked-trials-section__stat-box--neutral">
          <div className="linked-trials-section__coverage-label">Fields</div>
          <div className="linked-trials-section__stat-number linked-trials-section__stat-number--neutral">
            {fieldCount}
          </div>
        </div>
      </div>

      <button
        type="button"
        className="linked-trials-section__toggle"
        onClick={() => {
          setExpanded((prev) => !prev);
        }}
      >
        <span className="linked-trials-toggle">
          <ChevronIcon expanded={expanded} />
          <span className="linked-trials-section__toggle-label">
            Show Linked Trials
          </span>
        </span>
        <span className="linked-trials-section__toggle-count">
          {totalTrials} Trials
        </span>
      </button>

      {expanded && (
        <Table
          className="linked-trials-section__table"
          columns={columns}
          dataSource={linkedTrials}
          rowKey="id"
          pagination={false}
          size="small"
        />
      )}
    </div>
  );
};

export default LinkedTrialsSection;
